// @ts-check

import { html } from "lit-html";
import {
  renderDetailRow,
  renderErrorMessage,
  renderInputField,
  renderPlaceholder,
  renderSectionCard,
  renderSelectField,
} from "../ui.js";
import { formatEnglishNumber, hasText, parseNumber } from "../utils.js";

/** @typedef {ReturnType<typeof html>} LitView */
/** @typedef {import("./index.js").ToolEnv} ToolEnv */
/** @typedef {ReturnType<typeof createPercentageCalculatorState>} PercentageCalculatorState */
/** @typedef {{ kind: "empty" } | { kind: "error", error: string } | { kind: "ready", text: string, detail: string }} CalculationResult */
/** @typedef {{ kind: "empty" } | { kind: "error", error: string } | { kind: "ready", basePrice: number, taxAmount: number, totalPrice: number, taxPercent: number }} TaxResult */

export const percentageCalculatorTool = {
  key: "percentage",
  path: "/percentage-calculator",
  title: "Percentage Calculator",
  blurb: "Percent of a value, reverse percentages, changes, and tax totals.",
  badge: "Math",
  accentClass: "bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300",
  createState: createPercentageCalculatorState,
  render: renderPercentageCalculatorPage,
};

function createPercentageCalculatorState() {
  return {
    percent1: "",
    value1: "",
    value2: "",
    total2: "",
    from: "",
    to: "",
    amount4: "",
    percent4: "",
    price: "",
    taxPercent: "",
    taxOperation: "add",
  };
}

/**
 * @param {PercentageCalculatorState} toolState
 * @param {ToolEnv} env
 * @returns {LitView}
 */
function renderPercentageCalculatorPage(toolState, env) {
  const percentOfResult = computePercentOfResult(toolState.percent1, toolState.value1);
  const isPercentOfResult = computeIsPercentOfResult(toolState.value2, toolState.total2);
  const percentageChangeResult = computePercentageChangeResult(toolState.from, toolState.to);
  const wholeResult = computeWholeResult(toolState.amount4, toolState.percent4);
  const taxResult = computeTaxResult(
    toolState.price,
    toolState.taxPercent,
    toolState.taxOperation,
  );

  return html`
    <section class="grid gap-4 xl:grid-cols-2 xl:items-start">
      ${renderSectionCard(
        "What is X% of Y?",
        "Calculate a percentage slice of a base value.",
        html`
          <div class="grid gap-4 sm:grid-cols-2">
            ${renderInputField({
              label: "Percent",
              value: toolState.percent1,
              placeholder: "15",
              onInput: function (event) {
                env.handleFieldInput(toolState, "percent1", event);
              },
            })}
            ${renderInputField({
              label: "Value",
              value: toolState.value1,
              placeholder: "240",
              onInput: function (event) {
                env.handleFieldInput(toolState, "value1", event);
              },
            })}
          </div>
          <div class="mt-4">${renderCalculationResult(percentOfResult, "Enter both numbers to calculate.")}</div>
        `,
      )}

      ${renderSectionCard(
        "X is what percent of Y?",
        "Reverse the calculation and recover the percentage.",
        html`
          <div class="grid gap-4 sm:grid-cols-2">
            ${renderInputField({
              label: "Value",
              value: toolState.value2,
              placeholder: "30",
              onInput: function (event) {
                env.handleFieldInput(toolState, "value2", event);
              },
            })}
            ${renderInputField({
              label: "Total",
              value: toolState.total2,
              placeholder: "120",
              onInput: function (event) {
                env.handleFieldInput(toolState, "total2", event);
              },
            })}
          </div>
          <div class="mt-4">${renderCalculationResult(isPercentOfResult, "Enter a value and a total to calculate.")}</div>
        `,
      )}

      ${renderSectionCard(
        "Percentage Increase or Decrease",
        "See how much one value changed relative to another.",
        html`
          <div class="grid gap-4 sm:grid-cols-2">
            ${renderInputField({
              label: "From",
              value: toolState.from,
              placeholder: "80",
              onInput: function (event) {
                env.handleFieldInput(toolState, "from", event);
              },
            })}
            ${renderInputField({
              label: "To",
              value: toolState.to,
              placeholder: "100",
              onInput: function (event) {
                env.handleFieldInput(toolState, "to", event);
              },
            })}
          </div>
          <div class="mt-4">${renderCalculationResult(percentageChangeResult, "Enter both values to compare the change.")}</div>
        `,
      )}

      ${renderSectionCard(
        "If X is Y%, what is 100%?",
        "Recover the full value from a partial percentage.",
        html`
          <div class="grid gap-4 sm:grid-cols-2">
            ${renderInputField({
              label: "Amount",
              value: toolState.amount4,
              placeholder: "48",
              onInput: function (event) {
                env.handleFieldInput(toolState, "amount4", event);
              },
            })}
            ${renderInputField({
              label: "Percent",
              value: toolState.percent4,
              placeholder: "20",
              onInput: function (event) {
                env.handleFieldInput(toolState, "percent4", event);
              },
            })}
          </div>
          <div class="mt-4">${renderCalculationResult(wholeResult, "Enter the known amount and percentage to recover the whole.")}</div>
        `,
      )}

      ${renderSectionCard(
        "Tax Calculator",
        "Add tax to a price or work backwards from a tax-included total.",
        html`
          <div class="grid gap-4 lg:grid-cols-3">
            ${renderInputField({
              label: "Price",
              value: toolState.price,
              placeholder: "100",
              onInput: function (event) {
                env.handleFieldInput(toolState, "price", event);
              },
            })}
            ${renderInputField({
              label: "Tax Percentage",
              value: toolState.taxPercent,
              placeholder: "21",
              onInput: function (event) {
                env.handleFieldInput(toolState, "taxPercent", event);
              },
            })}
            ${renderSelectField({
              label: "Operation",
              value: toolState.taxOperation,
              onInput: function (event) {
                env.handleFieldInput(toolState, "taxOperation", event);
              },
              options: [
                { value: "add", label: "Add tax to price" },
                { value: "subtract", label: "Subtract tax from price" },
              ],
            })}
          </div>
          <div class="mt-4">${renderTaxResult(taxResult)}</div>
        `,
      )}
    </section>
  `;
}

/**
 * @param {CalculationResult} result
 * @param {string} placeholderText
 * @returns {LitView}
 */
function renderCalculationResult(result, placeholderText) {
  if (result.kind === "empty") {
    return renderPlaceholder(placeholderText);
  }

  if (result.kind === "error") {
    return renderErrorMessage(result.error);
  }

  return html`
    <div class="rounded-lg bg-stone-100 px-4 py-4">
      <div class="text-2xl font-semibold tracking-tight text-stone-900">${result.text}</div>
      <div class="mt-1.5 text-sm text-stone-500">${result.detail}</div>
    </div>
  `;
}

/**
 * @param {TaxResult} result
 * @returns {LitView}
 */
function renderTaxResult(result) {
  if (result.kind === "empty") {
    return renderPlaceholder("Enter price and tax percentage to see the breakdown.");
  }

  if (result.kind === "error") {
    return renderErrorMessage(result.error);
  }

  return html`
    <div class="rounded-lg bg-stone-100 p-4">
      <div class="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto]">
        ${renderDetailRow("Base price", formatEnglishNumber(result.basePrice, 2))}
        ${renderDetailRow(`Tax amount (${formatEnglishNumber(result.taxPercent, 2)}%)`, formatEnglishNumber(result.taxAmount, 2))}
        ${renderDetailRow("Total price", formatEnglishNumber(result.totalPrice, 2), "", true)}
      </div>
    </div>
  `;
}

/**
 * @param {string} percentText
 * @param {string} valueText
 * @returns {CalculationResult}
 */
function computePercentOfResult(percentText, valueText) {
  if (!hasText(percentText) || !hasText(valueText)) {
    return { kind: "empty" };
  }

  const percent = parseNumber(percentText);
  const value = parseNumber(valueText);
  if (!Number.isFinite(percent) || !Number.isFinite(value)) {
    return { kind: "error", error: "Please enter valid numbers." };
  }

  const result = (percent / 100) * value;
  return {
    kind: "ready",
    text: formatEnglishNumber(result, 2),
    detail: `${formatEnglishNumber(percent, 2)}% of ${formatEnglishNumber(value, 2)}`,
  };
}

/**
 * @param {string} valueText
 * @param {string} totalText
 * @returns {CalculationResult}
 */
function computeIsPercentOfResult(valueText, totalText) {
  if (!hasText(valueText) || !hasText(totalText)) {
    return { kind: "empty" };
  }

  const value = parseNumber(valueText);
  const total = parseNumber(totalText);
  if (!Number.isFinite(value) || !Number.isFinite(total)) {
    return { kind: "error", error: "Please enter valid numbers." };
  }

  if (total === 0) {
    return { kind: "error", error: "Total cannot be zero." };
  }

  const result = (value / total) * 100;
  return {
    kind: "ready",
    text: `${formatEnglishNumber(result, 2)}%`,
    detail: `${formatEnglishNumber(value, 2)} is ${formatEnglishNumber(result, 2)}% of ${formatEnglishNumber(total, 2)}`,
  };
}

/**
 * @param {string} fromText
 * @param {string} toText
 * @returns {CalculationResult}
 */
function computePercentageChangeResult(fromText, toText) {
  if (!hasText(fromText) || !hasText(toText)) {
    return { kind: "empty" };
  }

  const fromValue = parseNumber(fromText);
  const toValue = parseNumber(toText);
  if (!Number.isFinite(fromValue) || !Number.isFinite(toValue)) {
    return { kind: "error", error: "Please enter valid numbers." };
  }

  if (fromValue === 0) {
    return { kind: "error", error: "Starting value cannot be zero." };
  }

  const change = ((toValue - fromValue) / Math.abs(fromValue)) * 100;
  let changeType = "no change";
  if (change > 0) {
    changeType = "increase";
  }

  if (change < 0) {
    changeType = "decrease";
  }

  return {
    kind: "ready",
    text: `${formatEnglishNumber(Math.abs(change), 2)}% ${changeType}`,
    detail: `${formatEnglishNumber(fromValue, 2)} → ${formatEnglishNumber(toValue, 2)}`,
  };
}

/**
 * @param {string} amountText
 * @param {string} percentText
 * @returns {CalculationResult}
 */
function computeWholeResult(amountText, percentText) {
  if (!hasText(amountText) || !hasText(percentText)) {
    return { kind: "empty" };
  }

  const amount = parseNumber(amountText);
  const percent = parseNumber(percentText);
  if (!Number.isFinite(amount) || !Number.isFinite(percent)) {
    return { kind: "error", error: "Please enter valid numbers." };
  }

  if (percent === 0) {
    return { kind: "error", error: "Percentage cannot be zero." };
  }

  const whole = (amount / percent) * 100;
  return {
    kind: "ready",
    text: formatEnglishNumber(whole, 2),
    detail: `${formatEnglishNumber(amount, 2)} is ${formatEnglishNumber(percent, 2)}% of the whole`,
  };
}

/**
 * @param {string} priceText
 * @param {string} taxPercentText
 * @param {string} operation
 * @returns {TaxResult}
 */
function computeTaxResult(priceText, taxPercentText, operation) {
  if (!hasText(priceText) || !hasText(taxPercentText)) {
    return { kind: "empty" };
  }

  const price = parseNumber(priceText);
  const taxPercent = parseNumber(taxPercentText);
  if (!Number.isFinite(price) || !Number.isFinite(taxPercent)) {
    return { kind: "error", error: "Please enter valid numbers." };
  }

  let basePrice = 0;
  let taxAmount = 0;
  let totalPrice = 0;
  if (operation === "add") {
    basePrice = price;
    taxAmount = price * (taxPercent / 100);
    totalPrice = basePrice + taxAmount;
  } else {
    const divisor = 1 + taxPercent / 100;
    if (divisor === 0) {
      return { kind: "error", error: "Tax percentage cannot make the divisor zero." };
    }

    totalPrice = price;
    basePrice = totalPrice / divisor;
    taxAmount = totalPrice - basePrice;
  }

  return {
    kind: "ready",
    basePrice,
    taxAmount,
    totalPrice,
    taxPercent,
  };
}
